import React, { useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SALES_SMS_TEMPLATE_TYPE_LABEL, SalesSmsTemplateType, type SalesSmsTemplate } from "@fitness-app/data-models";

import { supabase } from "~/store/initializeStore";

const templatesQueryKey = ["salesSmsTemplates"] as const;

const TYPE_ORDER: SalesSmsTemplateType[] = [
  SalesSmsTemplateType.DayBefore,
  SalesSmsTemplateType.Morning,
  SalesSmsTemplateType.HourBefore,
];

const TEMPLATE_VARIABLES = [
  "{imie}",
  "{godzina}",
  "{data}",
  "{dzien}",
  "{nazwa_spotkania}",
  "{imie_sprzedawcy}",
  "{nazwisko_sprzedawcy}",
];

type TemplateFormValues = {
  type: SalesSmsTemplateType;
  name: string;
  body: string;
  enabled: boolean;
  sortOrder: number;
};

export const SalesSmsTemplates = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<TemplateFormValues>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<SalesSmsTemplate | null>(null);

  const templatesQuery = useQuery({
    queryKey: templatesQueryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_sms_template")
        .select("*")
        .order("type", { ascending: true })
        .order("sortOrder", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as SalesSmsTemplate[];
    },
  });

  const templates = templatesQuery.data ?? [];
  const templatesByType = useMemo(
    () =>
      TYPE_ORDER.map((type) => ({
        type,
        items: templates.filter((template) => template.type === type),
      })),
    [templates],
  );

  const saveMutation = useMutation({
    mutationFn: async (values: TemplateFormValues) => {
      const payload = {
        type: values.type,
        name: values.name,
        body: values.body,
        enabled: values.enabled,
        sortOrder: Number(values.sortOrder ?? 0),
        updatedAt: new Date().toISOString(),
      };

      if (activeTemplate) {
        const { error } = await supabase.from("sales_sms_template").update(payload).eq("id", activeTemplate.id);
        if (error) throw new Error(error.message);
        return;
      }

      const { error } = await supabase.from("sales_sms_template").insert(payload);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditorOpen(false);
      setActiveTemplate(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
      void message.success("Szablon zapisany");
    },
    onError: (error) => void message.error(error instanceof Error ? error.message : "Nie udało się zapisać szablonu"),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ template, enabled }: { template: SalesSmsTemplate; enabled: boolean }) => {
      const { error } = await supabase
        .from("sales_sms_template")
        .update({ enabled, updatedAt: new Date().toISOString() })
        .eq("id", template.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templatesQueryKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (template: SalesSmsTemplate) => {
      const { error } = await supabase.from("sales_sms_template").delete().eq("id", template.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: templatesQueryKey });
      void message.success("Szablon usunięty");
    },
  });

  const openNew = (type: SalesSmsTemplateType) => {
    setActiveTemplate(null);
    // Max existing order + 10 so deleting a template can't produce a duplicate sortOrder.
    const nextOrder =
      templates
        .filter((template) => template.type === type)
        .reduce((max, template) => Math.max(max, template.sortOrder), 0) + 10;
    form.setFieldsValue({ type, name: "", body: "", enabled: true, sortOrder: nextOrder });
    setEditorOpen(true);
  };

  const openEdit = (template: SalesSmsTemplate) => {
    setActiveTemplate(template);
    form.setFieldsValue({
      type: template.type,
      name: template.name,
      body: template.body,
      enabled: template.enabled,
      sortOrder: template.sortOrder,
    });
    setEditorOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div>
          <Typography.Title level={5} className="!mb-1">
            Szablony SMS
          </Typography.Title>
          <Typography.Text type="secondary">
            Gotowe wiadomości używane w aplikacji mobilnej. Zmienne:{" "}
            {TEMPLATE_VARIABLES.map((variable) => (
              <Tag key={variable} className="font-mono">
                {variable}
              </Tag>
            ))}
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openNew(SalesSmsTemplateType.DayBefore)}>
          Dodaj szablon
        </Button>
      </div>

      {templatesQuery.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Nie udało się pobrać szablonów"
          description={(templatesQuery.error as Error).message}
        />
      ) : null}

      {templatesByType.map(({ type, items }) => (
        <div key={type} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <Space>
              <Typography.Text strong>{SALES_SMS_TEMPLATE_TYPE_LABEL[type]}</Typography.Text>
              <Tag>{items.length}</Tag>
            </Space>
            <Button size="small" icon={<PlusOutlined />} onClick={() => openNew(type)}>
              Dodaj
            </Button>
          </div>

          {items.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak szablonów tego typu" />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((template) => (
                <div key={template.id} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <Space size="small" className="mb-1">
                      <Typography.Text strong>{template.name}</Typography.Text>
                      {!template.enabled ? <Tag>nieaktywny</Tag> : null}
                    </Space>
                    <Typography.Paragraph type="secondary" className="!mb-0 whitespace-pre-wrap">
                      {template.body}
                    </Typography.Paragraph>
                  </div>
                  <Space>
                    <Switch
                      size="small"
                      checked={template.enabled}
                      loading={toggleMutation.isLoading && toggleMutation.variables?.template.id === template.id}
                      onChange={(enabled) => toggleMutation.mutate({ template, enabled })}
                    />
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(template)}>
                      Edytuj
                    </Button>
                    <Popconfirm
                      title="Usunąć szablon?"
                      okText="Usuń"
                      cancelText="Anuluj"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteMutation.mutate(template)}
                      disabled={deleteMutation.isLoading && deleteMutation.variables?.id === template.id}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deleteMutation.isLoading && deleteMutation.variables?.id === template.id}
                      />
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <Drawer
        title={activeTemplate ? "Edytuj szablon" : "Nowy szablon"}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        width={560}
        extra={
          <Button type="primary" loading={saveMutation.isLoading} onClick={() => form.submit()}>
            Zapisz
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="type" label="Typ" rules={[{ required: true }]}>
              <Select
                options={TYPE_ORDER.map((type) => ({ value: type, label: SALES_SMS_TEMPLATE_TYPE_LABEL[type] }))}
              />
            </Form.Item>
            <Form.Item name="name" label="Nazwa" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="sortOrder" label="Kolejność">
              <InputNumber className="w-full" />
            </Form.Item>
            <Form.Item name="enabled" label="Aktywny" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <Form.Item
            name="body"
            label="Treść wiadomości"
            rules={[{ required: true }]}
            extra={`Dostępne zmienne: ${TEMPLATE_VARIABLES.join(", ")}`}
          >
            <Input.TextArea rows={5} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};
